import { useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Icon } from "./Icon";
import { supabase } from "../lib/supabase";
import { useCategories } from "../hooks/useCategories";

// Upgrade an existing customer account into a seller account. Inserts a
// sellers row (status='active' — store goes live immediately) linked to the
// current user_id, then flips users.role to 'business'. On success the caller
// should trigger useAuth().refresh() so the app shell picks up seller mode
// without a page reload.
//
// Security: RLS `sellers_insert_own` gates INSERT on `user_id = auth.uid()` and
// `users_update_own` gates the role flip. No service-role / Edge Function
// needed — this is strictly a self-service upgrade for an authenticated user.
// Admins can still suspend stores after the fact via SellersTab.
const PAYMENT_OPTIONS = [
  { id: "airtel", label: "Airtel Money" },
  { id: "mtn", label: "MTN MoMo" },
  { id: "visa", label: "Visa" },
  { id: "mastercard", label: "Mastercard" },
];

const DELIVERY_OPTIONS = [
  { id: "pickup", label: "Pickup" },
  { id: "courier", label: "Courier" },
];

const LOCATION_TYPES = [
  { id: "campus", label: "On Campus" },
  { id: "physical", label: "Physical Storefront" },
  { id: "online", label: "Online Only" },
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function BecomeSellerModal({ open, onClose, userId, onSuccess }) {
  const { categories } = useCategories();
  const [storeName, setStoreName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [locationType, setLocationType] = useState("campus");
  const [address, setAddress] = useState("");
  const [paymentMethods, setPaymentMethods] = useState(["airtel", "mtn"]);
  const [deliveryOptions, setDeliveryOptions] = useState(["pickup"]);
  const [deliveryFeeKwacha, setDeliveryFeeKwacha] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // "All" pseudo-category is inserted at the top of the list by useCategories —
  // drop it here since sellers pick a real vertical.
  const realCategories = useMemo(
    () => (categories || []).filter((c) => c.id !== "all"),
    [categories]
  );

  const canSubmit =
    storeName.trim().length >= 2 &&
    !!category &&
    paymentMethods.length > 0 &&
    deliveryOptions.length > 0 &&
    !busy;

  function togglePayment(id) {
    setPaymentMethods((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleDelivery(id) {
    setDeliveryOptions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit || !userId) return;
    setBusy(true);
    setError(null);

    // store_slug is filled server-side by trg_set_seller_slug. The trigger
    // slugifies store_name and appends a user_id suffix on collision.
    const feeNgwee = Math.max(0, Math.round(Number(deliveryFeeKwacha || 0) * 100));

    const { data: sellerRow, error: insertErr } = await supabase
      .from("sellers")
      .insert({
        user_id: userId,
        store_name: storeName.trim(),
        description: description.trim() || null,
        category: category,
        location_type: locationType,
        address: locationType === "online" ? null : address.trim() || null,
        payment_methods: paymentMethods,
        delivery_options: deliveryOptions,
        delivery_fee: feeNgwee,
        status: "active",
        approved_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (insertErr || !sellerRow) {
      setBusy(false);
      setError(insertErr?.message || "Failed to create seller profile. Please try again.");
      return;
    }

    // Flip the user's role so CustomerTabs → SellerTabs toggles the correct
    // chrome. The sellers row is the source of truth for `isSeller` but role
    // still drives a few server-side checks.
    const { error: roleErr } = await supabase
      .from("users")
      .update({ role: "business" })
      .eq("id", userId);
    if (roleErr) {
      // Seller row succeeded — don't roll back, but surface the warning.
      setError("Seller profile created. Role update failed — please refresh.");
    }

    setBusy(false);
    onSuccess?.(sellerRow);
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          background: C.bg,
          borderRadius: 20,
          boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: C.white,
            borderBottom: "1px solid rgba(196,193,183,0.3)",
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.3px" }}>
              Open a store on Kapio
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
              Your store goes live as soon as you submit.
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6 }}
          >
            <Icon name="x" size={18} color={C.charcoal} />
          </button>
        </div>

        <form onSubmit={submit} style={{ padding: 22, overflowY: "auto", display: "grid", gap: 14 }}>
          <Field label="Store name">
            <input
              type="text"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              required
              minLength={2}
              maxLength={100}
              placeholder="e.g. Chisomo Designs"
              style={inputStyle}
              autoFocus
            />
            {storeName && (
              <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>
                URL: kapio/{slugify(storeName) || "—"}
              </div>
            )}
          </Field>

          <Field label="Category">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              <option value="">Select a category…</option>
              {realCategories.map((c) => (
                <option key={c.id} value={c.slug || c.name}>
                  {c.icon ? `${c.icon} ${c.name}` : c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Short description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="What do you sell?"
              style={{ ...inputStyle, resize: "vertical", minHeight: 68, fontFamily: "inherit" }}
            />
          </Field>

          <Field label="Where do you operate?">
            <div style={{ display: "flex", gap: 6, padding: 4, background: C.cream, borderRadius: 12 }}>
              {LOCATION_TYPES.map((l) => (
                <button
                  type="button"
                  key={l.id}
                  onClick={() => setLocationType(l.id)}
                  style={{
                    flex: 1,
                    padding: "9px 10px",
                    border: "none",
                    background: locationType === l.id ? C.white : "transparent",
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 800,
                    color: locationType === l.id ? C.charcoal : C.muted,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    boxShadow: locationType === l.id ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </Field>

          {locationType !== "online" && (
            <Field label={locationType === "campus" ? "Campus / hostel" : "Street address"}>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                maxLength={200}
                placeholder={locationType === "campus" ? "e.g. UNZA — Great East Road" : "e.g. 12 Cha Cha Cha Rd, Lusaka"}
                style={inputStyle}
              />
            </Field>
          )}

          <Field label="Accepts payments via">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {PAYMENT_OPTIONS.map((p) => {
                const on = paymentMethods.includes(p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => togglePayment(p.id)}
                    style={chipStyle(on)}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Delivery options">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {DELIVERY_OPTIONS.map((d) => {
                const on = deliveryOptions.includes(d.id);
                return (
                  <button
                    type="button"
                    key={d.id}
                    onClick={() => toggleDelivery(d.id)}
                    style={chipStyle(on)}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </Field>

          {deliveryOptions.includes("courier") && (
            <Field label="Courier delivery fee (Kwacha)">
              <input
                type="number"
                min={0}
                step={1}
                value={deliveryFeeKwacha}
                onChange={(e) => setDeliveryFeeKwacha(e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </Field>
          )}

          {error && (
            <div
              style={{
                padding: "10px 12px",
                background: C.redBg,
                border: `1px solid ${C.red}`,
                borderRadius: 10,
                fontSize: 12.5,
                color: C.red,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                flex: 1,
                padding: "12px 16px",
                border: `1px solid ${C.dustDk}`,
                background: C.white,
                color: C.charcoal,
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 800,
                cursor: busy ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                flex: 2,
                padding: "12px 16px",
                border: "none",
                background: canSubmit ? C.hunter : C.dustDk,
                color: C.dust,
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 800,
                cursor: canSubmit ? "pointer" : "not-allowed",
                fontFamily: "inherit",
              }}
            >
              {busy ? "Creating store…" : "Open my store"}
            </button>
          </div>

          <div style={{ fontSize: 10.5, color: C.muted, textAlign: "center", lineHeight: 1.5 }}>
            By submitting you agree to the 10% platform commission on every sale.
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.slate, marginBottom: 6, letterSpacing: 0.2 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  fontSize: 13.5,
  fontFamily: "inherit",
  background: C.cream,
  border: "1px solid rgba(196,193,183,0.4)",
  borderRadius: 12,
  color: C.charcoal,
  outline: "none",
  boxSizing: "border-box",
};

function chipStyle(on) {
  return {
    padding: "8px 14px",
    border: on ? `1px solid ${C.hunter}` : `1px solid ${C.dustDk}`,
    background: on ? C.greenBg : C.white,
    color: on ? C.hunter : C.charcoal,
    borderRadius: 10,
    fontSize: 12.5,
    fontWeight: on ? 800 : 700,
    cursor: "pointer",
    fontFamily: "inherit",
  };
}
